import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase-init';
import { startProduction } from '../../services/shop-service';
import { statusLabel, statusColor } from '../../utils/job-status';
import {
  ClipboardList, LayoutGrid, Frame, Wrench, ZoomIn, ZoomOut, Printer, ExternalLink,
  Download, ArrowLeft, FileText, FileX, PlayCircle, Loader2,
} from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const LINE   = 'rgba(43, 38, 32, 0.09)';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const DOC_TABS = [
  { key: 'cutlist',    label: 'Cut List',    Icon: ClipboardList },
  { key: 'glassorder', label: 'Glass Order', Icon: LayoutGrid },
  { key: 'flexscreen', label: 'FlexScreen',  Icon: Frame },
  { key: 'shoporder',  label: 'Shop Order',  Icon: Wrench },
];

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}
function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
function isMobileDevice() {
  return isAndroid() || isIOS();
}

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [pdfUrls, setPdfUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [isWide, setIsWide] = useState(window.innerWidth >= 900);
  const iframeRef = useRef(null);

  useEffect(() => {
    function handleResize() { setIsWide(window.innerWidth >= 900); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function loadJob() {
      try {
        const snap = await getDoc(doc(db, 'jobs', jobId));
        if (snap.exists()) {
          const jobData = { id: snap.id, ...snap.data() };
          let urls = {};
          if (jobData.cutlistJobId) {
            const cutSnap = await getDoc(doc(db, 'cutlistJobs', jobData.cutlistJobId));
            if (cutSnap.exists()) urls = cutSnap.data().pdfUrls || {};
          }
          setJob(jobData);
          setPdfUrls(urls);
          const firstAvail = DOC_TABS.find(t => urls[t.key]);
          if (firstAvail) { setActiveTab(firstAvail.key); setPdfLoading(true); }
        }
      } catch (e) {
        console.error('Failed to load job:', e);
      } finally {
        setLoading(false);
      }
    }
    loadJob();
  }, [jobId]);

  const availableTabs = DOC_TABS.filter(t => pdfUrls[t.key]);
  const hasPDFs = availableTabs.length > 0;
  const currentPdfUrl = activeTab ? pdfUrls[activeTab] : null;
  const currentTabLabel = availableTabs.find(t => t.key === activeTab)?.label || 'Document';

  function getInlineUrl(url) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return url + separator + 'response-content-disposition=inline';
  }

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/shop');
    }
  }

  function handlePrint() {
    if (!currentPdfUrl) return;
    var f = document.createElement('iframe');
    f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
    f.src = currentPdfUrl;
    document.body.appendChild(f);
    f.onload = function() {
      setTimeout(function() {
        try { f.contentWindow.print(); } catch(e) { window.open(currentPdfUrl, '_blank'); }
        setTimeout(function() { document.body.removeChild(f); }, 1000);
      }, 500);
    };
    f.onerror = function() { window.open(currentPdfUrl, '_blank'); document.body.removeChild(f); };
  }

  function handleOpenPdf() {
    if (!currentPdfUrl) return;
    if (isAndroid()) {
      window.open('https://docs.google.com/gview?url=' + encodeURIComponent(currentPdfUrl) + '&embedded=false', '_blank');
    } else {
      window.open(getInlineUrl(currentPdfUrl), '_blank');
    }
  }

  function handleDownloadPdf() {
    if (!currentPdfUrl) return;
    const a = document.createElement('a');
    a.href = currentPdfUrl;
    a.download = `${currentTabLabel.replace(/\s+/g, '_')}_WO${job?.woNumber || job?.contractNumber || ''}.pdf`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function openSpecificPdf(url) {
    if (!url) return;
    if (isAndroid()) {
      window.open('https://docs.google.com/gview?url=' + encodeURIComponent(url) + '&embedded=false', '_blank');
    } else {
      const separator = url.includes('?') ? '&' : '?';
      window.open(url + separator + 'response-content-disposition=inline', '_blank');
    }
  }

  const InfoCard = ({ label, value }) => (
    <div style={{ padding: '16px 18px', background: '#fff', borderRadius: '14px', marginBottom: '10px', border: `1px solid ${LINE}` }}>
      <div style={{ fontSize: '11px', color: STONE, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '16px', fontWeight: '600', color: INK, lineHeight: '1.4' }}>{value}</div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: PAPER, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', fontFamily: FONT_SANS }}>
      <Loader2 size={22} strokeWidth={2} color={WALNUT} style={{ animation: 'twpShopDetailSpin 0.9s linear infinite' }} />
      <div style={{ fontSize: '15px', color: STONE }}>Loading…</div>
      <style>{`@keyframes twpShopDetailSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!job) return (
    <div style={{ minHeight: '100vh', background: PAPER, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_SANS }}>
      <div style={{ fontSize: '16px', color: STONE }}>Job not found</div>
    </div>
  );

  const accent = statusColor(job.status);

  const PdfPanel = () => {
    const android = isAndroid();
    const ios = isIOS();
    const mobile = isMobileDevice();

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, fontFamily: FONT_SANS }}>
        <div style={{ display: 'flex', padding: '10px 12px 0', gap: '6px', background: '#fff', borderBottom: `1px solid ${LINE}`, flexShrink: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {availableTabs.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key}
                onClick={() => { setActiveTab(tab.key); setPdfLoading(true); setZoom(1); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  padding: '12px 16px', borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer', fontFamily: FONT_SANS,
                  fontSize: '14px', fontWeight: isActive ? '700' : '500', whiteSpace: 'nowrap', flexShrink: 0,
                  background: isActive ? WALNUT : 'rgba(43,38,32,0.04)', color: isActive ? 'white' : STONE,
                  borderBottom: isActive ? `3px solid ${WALNUT}` : '3px solid transparent'
                }}>
                <tab.Icon size={14} strokeWidth={2.25} /> {tab.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(43,38,32,0.03)', borderBottom: `1px solid ${LINE}`, flexShrink: 0, flexWrap: 'wrap' }}>
          {!isWide && (
            <button onClick={() => setShowPdf(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${LINE}`, background: '#fff', cursor: 'pointer', fontSize: '13px', fontFamily: FONT_SANS, fontWeight: '600', color: INK }}>
              <ArrowLeft size={14} strokeWidth={2.25} /> Back
            </button>
          )}
          {!android && (
            <button onClick={handlePrint}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', border: `1px solid ${LINE}`, background: '#fff', cursor: 'pointer', fontSize: '13px', fontFamily: FONT_SANS, fontWeight: '600', color: INK }}>
              <Printer size={14} strokeWidth={2.25} /> Print
            </button>
          )}
          {!android && !ios && (
            <>
              <button onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
                style={{ display: 'flex', alignItems: 'center', padding: '10px', borderRadius: '10px', border: `1px solid ${LINE}`, background: '#fff', cursor: 'pointer' }}>
                <ZoomIn size={15} strokeWidth={2.25} color={INK} />
              </button>
              <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
                style={{ display: 'flex', alignItems: 'center', padding: '10px', borderRadius: '10px', border: `1px solid ${LINE}`, background: '#fff', cursor: 'pointer' }}>
                <ZoomOut size={15} strokeWidth={2.25} color={INK} />
              </button>
              <span style={{ fontSize: '12.5px', color: STONE }}>{Math.round(zoom * 100)}%</span>
            </>
          )}
          <button onClick={handleOpenPdf}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', background: WALNUT, color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: FONT_SANS, fontWeight: '600', marginLeft: mobile ? 0 : 'auto' }}>
            <ExternalLink size={14} strokeWidth={2.25} /> Open in New Tab
          </button>
          <button onClick={handleDownloadPdf}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', background: '#2f5f57', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: FONT_SANS, fontWeight: '600' }}>
            <Download size={14} strokeWidth={2.25} /> Download
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', position: 'relative', background: '#1a1a1a', WebkitOverflowScrolling: 'touch' }}>
          {android ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px 20px', textAlign: 'center', color: '#888' }}>
              <FileText size={48} strokeWidth={1.5} style={{ marginBottom: '18px', opacity: 0.7 }} />
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#eee', marginBottom: '8px' }}>{currentTabLabel}</div>
              <div style={{ fontSize: '13px', color: '#999', marginBottom: '22px', lineHeight: '1.6', maxWidth: '300px' }}>
                Tap a button below to view this document. Chrome on Android opens PDFs best in a new tab.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '280px' }}>
                <button onClick={handleOpenPdf}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '15px', borderRadius: '12px', background: WALNUT, color: 'white', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '700', fontFamily: FONT_SANS, boxShadow: `0 4px 12px ${WALNUT}4d` }}>
                  <ExternalLink size={15} strokeWidth={2.25} /> Open PDF
                </button>
                <button onClick={handleDownloadPdf}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '15px', borderRadius: '12px', background: 'transparent', color: '#eee', border: '2px solid rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: '15px', fontWeight: '700', fontFamily: FONT_SANS }}>
                  <Download size={15} strokeWidth={2.25} /> Download PDF
                </button>
              </div>
            </div>
          ) : (
            <>
              {pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a', zIndex: 2 }}>
                  <div style={{ color: '#555', fontSize: '13px', letterSpacing: '0.05em' }}>Loading document…</div>
                </div>
              )}
              <iframe
                ref={iframeRef}
                key={activeTab}
                src={ios ? getInlineUrl(currentPdfUrl) : currentPdfUrl}
                onLoad={() => setPdfLoading(false)}
                style={{ width: `${100 * zoom}%`, height: `${100 * zoom}%`, minWidth: '100%', minHeight: '100%', border: 'none', display: 'block' }}
                title={activeTab}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const InfoPanel = () => (
    <div style={{ padding: '20px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: isWide ? 'none' : 1, width: isWide ? '340px' : '100%', borderRight: isWide ? `1px solid ${LINE}` : 'none', fontFamily: FONT_SANS }}>
      {[
        { label: 'Job Type', value: job.jobType },
        { label: 'Windows', value: job.windowCount > 0 ? `${job.windowCount} window${job.windowCount !== 1 ? 's' : ''}` : null },
        { label: 'Doors', value: job.doorCount > 0 ? `${job.doorCount} door${job.doorCount !== 1 ? 's' : ''}` : null },
        { label: 'Sold Date', value: job.soldDate?.toDate ? job.soldDate.toDate().toLocaleDateString() : job.soldDate },
        { label: 'Address', value: [job.address, job.city, job.stateZip].filter(Boolean).join(', ') },
        { label: 'Phone', value: job.phone },
      ].filter(r => r.value).map(r => <InfoCard key={r.label} label={r.label} value={r.value} />)}

      {job.notes && (
        <div style={{ padding: '16px 18px', background: 'rgba(166,117,42,0.06)', border: '1px solid rgba(166,117,42,0.2)', borderRadius: '14px', marginBottom: '10px' }}>
          <div style={{ fontSize: '11px', color: STONE, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Notes</div>
          <div style={{ fontSize: '14px', color: INK, lineHeight: '1.7' }}>{job.notes}</div>
        </div>
      )}

      {job.status === 'awaiting_production' && (
        <button
          onClick={() => setConfirmStart(true)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '15px', background: WALNUT, border: 'none', borderRadius: '14px', color: 'white', fontSize: '16px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS, marginTop: '8px', marginBottom: '8px', boxShadow: `0 4px 12px ${WALNUT}4d` }}>
          <PlayCircle size={17} strokeWidth={2.25} /> Start Production
        </button>
      )}

      {confirmStart && (
        <div onClick={() => setConfirmStart(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(30, 25, 20, 0.62)', backdropFilter: 'blur(5px)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: PAPER, borderRadius: '20px', maxWidth: '400px', width: '100%', boxShadow: '0 30px 80px rgba(30, 25, 20, 0.35)', overflow: 'hidden' }}>
            <div style={{ height: '3px', background: WALNUT }} />
            <div style={{ padding: '26px 26px 22px', textAlign: 'center' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '14px', background: `${WALNUT}14`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <PlayCircle size={26} strokeWidth={2} color={WALNUT} />
              </div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: '600', color: INK, marginBottom: '8px' }}>Start Production?</div>
              <div style={{ fontSize: '13.5px', color: STONE, lineHeight: '1.5' }}>This will update the job status to <strong style={{ color: INK }}>In Production</strong> and notify the customer.</div>
            </div>
            <div style={{ padding: '0 26px 26px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => setConfirmStart(false)} style={{ padding: '13px', background: '#fff', border: `1.5px solid ${LINE}`, borderRadius: '11px', color: STONE, fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}>
                Cancel
              </button>
              <button
                disabled={starting}
                onClick={async () => {
                  setStarting(true);
                  try {
                    const workerName = job.assignedCrew || 'Shop Worker';
                    await startProduction(job.id, workerName);
                    setConfirmStart(false);
                    setJob(prev => ({ ...prev, status: 'in_production' }));
                  } catch(e) {
                    alert('Failed to start production. Try again.');
                  }
                  setStarting(false);
                }}
                style={{ padding: '13px', background: starting ? STONE : WALNUT, border: 'none', borderRadius: '11px', color: 'white', fontSize: '14px', fontWeight: '700', cursor: starting ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS }}>
                {starting ? 'Starting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasPDFs && !isWide && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Documents</div>
          {availableTabs.map(tab => (
            <button key={tab.key} onClick={() => {
              setActiveTab(tab.key);
              if (isAndroid()) {
                openSpecificPdf(pdfUrls[tab.key]);
              } else {
                setShowPdf(true);
              }
            }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 18px', background: '#fff', border: `1.5px solid ${LINE}`, borderRadius: '12px', cursor: 'pointer', fontFamily: FONT_SANS, fontSize: '14px', fontWeight: '700', color: WALNUT }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><tab.Icon size={15} strokeWidth={2.25} /> {tab.label}</span>
              <span style={{ fontSize: '12px', color: STONE, fontWeight: '500' }}>Open</span>
            </button>
          ))}
        </div>
      )}

      {hasPDFs && isWide && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Documents</div>
          {availableTabs.map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPdfLoading(true); setZoom(1); }}
              style={{
                width: '100%', padding: '12px 16px', background: activeTab === tab.key ? `${WALNUT}14` : '#fff',
                border: activeTab === tab.key ? `1.5px solid ${WALNUT}` : `1.5px solid ${LINE}`, borderRadius: '10px', cursor: 'pointer',
                fontFamily: FONT_SANS, fontSize: '13.5px', fontWeight: '600', color: activeTab === tab.key ? WALNUT : STONE,
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
              <tab.Icon size={14} strokeWidth={2.25} /> {tab.label}
            </button>
          ))}
        </div>
      )}

      {!hasPDFs && (
        <div style={{ padding: '24px', background: '#fff', border: `1px solid ${LINE}`, borderRadius: '14px', textAlign: 'center', color: STONE, marginTop: '8px' }}>
          <FileX size={30} strokeWidth={1.75} style={{ marginBottom: '10px', opacity: 0.6 }} />
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px', color: INK }}>No documents yet</div>
          <div style={{ fontSize: '12.5px' }}>PDFs will appear here once generated from the office</div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ height: '100vh', background: PAPER, display: 'flex', flexDirection: 'column', fontFamily: FONT_SANS, overflow: 'hidden' }}>
      <div style={{ background: WALNUT, padding: '0 16px', height: '64px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <button onClick={handleBack}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.16)', border: 'none', borderRadius: '10px', color: 'white', padding: '9px 14px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}>
          <ArrowLeft size={15} strokeWidth={2.25} /> Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DISPLAY, color: 'white', fontWeight: '600', fontSize: '19px', lineHeight: 1 }}>WO# {job.woNumber || job.contractNumber || '—'}</div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.customerName || '—'}</div>
        </div>
        <div style={{ padding: '6px 12px', borderRadius: '20px', background: 'rgba(255,255,255,0.16)', border: `1px solid ${accent}`, color: 'white', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
          {statusLabel(job.status)}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {isWide ? (
          <>
            <InfoPanel />
            {hasPDFs && <PdfPanel />}
          </>
        ) : (
          showPdf && hasPDFs ? <PdfPanel /> : <InfoPanel />
        )}
      </div>
    </div>
  );
}
